import { MRT_ColumnDef, MaterialReactTable } from "material-react-table"
import { useCallback, useEffect, useMemo, useState } from "react"
import { GenerateCols } from "./utils"
import { useAppDispatch, useAppSelector } from "../../../redux/hooks"
import { pnlBudgetType } from "../../../pages/PNLBudget/types"
import { resetColumns, updateCell } from "../../../redux/slices/pnlBudgetSlice"
import { Box, Typography } from "@mui/material"
import { stringCols } from "./pnlTableConfig"
import { SimpleConfirmation } from "../../Modal/Reusable"

const PNLTable = () => {
  const dispatch = useAppDispatch()
  const state: pnlBudgetType = useAppSelector((state) => state.pnlBudget)
  const {
    budgetData,
    budgetYear,
    aggregations,
    meta,
    resourceBudgetPublished,
    dirtyCells,
  } = state
  const { loading } = useAppSelector((state) => state.app)
  const metricState: any = useAppSelector((store) => store.metric)
  const maevaSelectedClasses = metricState.maevaSelectedClassesNoAll
  const selectedClassLabel = maevaSelectedClasses?.[0]?.label

  // Flag to track if blur is caused by keyboard navigation
  const [isKeyboardNavigation, setIsKeyboardNavigation] = useState(false)

  // Helper function to flatten nested columns and get all editable columns
  const getFlattenedEditableColumns = () => {
    const flattenedColumns: any[] = []

    const flattenColumns = (columns: any[]) => {
      columns.forEach((col) => {
        if (col.columns) {
          // This is a grouped column, flatten its sub-columns
          flattenColumns(col.columns)
        } else {
          // This is a leaf column
          if (col.enableEditing !== false && col.accessorKey !== "line_item") {
            flattenedColumns.push(col)
          }
        }
      })
    }

    flattenColumns(mutableCols)
    return flattenedColumns
  }

  // Utility function to handle keyboard navigation
  const handleKeyboardNavigation = (
    event: React.KeyboardEvent,
    cell: any,
    table: any,
  ) => {
    if (event.key === "Escape") {
      // Escape: Exit editing mode
      event.preventDefault()
      table.setEditingCell(null)
      return
    }

    if (event.key === "Tab") {
      event.preventDefault()

      // Set flag to indicate keyboard navigation
      setIsKeyboardNavigation(true)

      // Save the current cell value before moving to the next cell
      const currentValue = (event.target as HTMLInputElement).value
      dispatch(
        updateCell({
          key: cell.column.id,
          index: cell.row.index,
          updateVal: currentValue,
          id: cell.id,
          original: cell.row.original,
        }),
      )

      const currentRowIndex = cell.row.index
      const currentColumnIndex = cell.column.id

      // Get all editable columns for the current row (excluding pinned columns)
      const editableColumns = getFlattenedEditableColumns()

      // Find current column index in editable columns
      const currentEditableIndex = editableColumns.findIndex(
        (col: any) => col.id === currentColumnIndex,
      )

      // If current column is not in editable columns, find the first editable column
      if (currentEditableIndex === -1) {
        if (editableColumns.length > 0) {
          const firstColumn = editableColumns[0]
          // Find the cell in the table's internal structure
          const firstCell = table
            .getRow(currentRowIndex)
            ?.getAllCells()
            ?.find((c: any) => c.column.id === firstColumn.id)
          if (firstCell) {
            table.setEditingCell(firstCell)
          }
        }
        return
      }

      if (event.shiftKey) {
        // Shift+Tab: Move to previous cell
        if (currentEditableIndex > 0) {
          // Move to previous column in same row
          const prevColumn = editableColumns[currentEditableIndex - 1]
          const prevCell = table
            .getRow(currentRowIndex)
            ?.getAllCells()
            ?.find((c: any) => c.column.id === prevColumn.id)
          if (prevCell) {
            table.setEditingCell(prevCell)
          }
        } else if (currentRowIndex > 1) {
          // Move to last column of previous row
          const prevRowIndex = currentRowIndex - 1
          const lastColumn = editableColumns[editableColumns.length - 1]
          const prevCell = table
            .getRow(prevRowIndex)
            ?.getAllCells()
            ?.find((c: any) => c.column.id === lastColumn.id)
          if (prevCell) {
            table.setEditingCell(prevCell)
          }
        }
      } else {
        // Tab: Move to next cell
        if (currentEditableIndex < editableColumns.length - 1) {
          // Move to next column in same row
          const nextColumn = editableColumns[currentEditableIndex + 1]
          const nextCell = table
            .getRow(currentRowIndex)
            ?.getAllCells()
            ?.find((c: any) => c.column.id === nextColumn.id)
          if (nextCell) {
            table.setEditingCell(nextCell)
          }
        } else if (currentRowIndex < budgetData.length - 1) {
          // Move to first column of next row
          const nextRowIndex = currentRowIndex + 1
          const firstColumn = editableColumns[0]
          const nextCell = table
            .getRow(nextRowIndex)
            ?.getAllCells()
            ?.find((c: any) => c.column.id === firstColumn.id)
          if (nextCell) {
            table.setEditingCell(nextCell)
          }
        }
      }
    } else if (event.key === "Enter" && !event.shiftKey) {
      // Enter: Move to first cell of next row
      event.preventDefault()

      // Set flag to indicate keyboard navigation
      setIsKeyboardNavigation(true)

      // Save the current cell value before moving to the next row
      const currentValue = (event.target as HTMLInputElement).value
      dispatch(
        updateCell({
          key: cell.column.id,
          index: cell.row.index,
          updateVal: currentValue,
          id: cell.id,
          original: cell.row.original,
        }),
      )

      const currentRowIndex = cell.row.index

      // Get all editable columns for the current row (excluding pinned columns)
      const editableColumns = getFlattenedEditableColumns()

      if (
        currentRowIndex < budgetData.length - 1 &&
        editableColumns.length > 0
      ) {
        // Move to first column of next row
        const nextRowIndex = currentRowIndex + 1
        const firstColumn = editableColumns[0]
        const nextCell = table
          .getRow(nextRowIndex)
          ?.getAllCells()
          ?.find((c: any) => c.column.id === firstColumn.id)
        if (nextCell) {
          table.setEditingCell(nextCell)
        }
      }
    } else if (event.key === "Enter" && event.shiftKey) {
      // Shift+Enter: Move to first cell of previous row
      event.preventDefault()

      // Set flag to indicate keyboard navigation
      setIsKeyboardNavigation(true)

      // Save the current cell value before moving to the previous row
      const currentValue = (event.target as HTMLInputElement).value
      dispatch(
        updateCell({
          key: cell.column.id,
          index: cell.row.index,
          updateVal: currentValue,
          id: cell.id,
          original: cell.row.original,
        }),
      )

      const currentRowIndex = cell.row.index

      // Get all editable columns for the current row (excluding pinned columns)
      const editableColumns = getFlattenedEditableColumns()

      if (currentRowIndex > 1 && editableColumns.length > 0) {
        // Move to first column of previous row
        const prevRowIndex = currentRowIndex - 1
        const firstColumn = editableColumns[0]
        const prevCell = table
          .getRow(prevRowIndex)
          ?.getAllCells()
          ?.find((c: any) => c.column.id === firstColumn.id)
        if (prevCell) {
          table.setEditingCell(prevCell)
        }
      }
    }
  }

  const {
    mutableCols,
    colsOrder,
    hasRevenueNotSet,
  }: {
    mutableCols: MRT_ColumnDef<any>[]
    colsOrder: string[]
    hasRevenueNotSet: boolean
  } = useMemo(() => {
    const { dynamicCols, colsOrder, hasRevenueNotSet } = GenerateCols(
      budgetYear || 2023,
      budgetData,
      aggregations,
      meta,
      resourceBudgetPublished,
      dirtyCells,
      selectedClassLabel,
    )
    return {
      mutableCols: dynamicCols,
      colsOrder: [...stringCols, ...colsOrder],
      hasRevenueNotSet,
    }
  }, [budgetYear, aggregations, budgetData, selectedClassLabel])

  const editable = useCallback(
    (row: any) => {
  // if( selectedClassLabel === "PSC"){
  //     return true;
  //   }      
  const disabledRows = [
        "DOCTOR COMPENSATION",
        "HYGIENE COMPENSATION",
        "FRONT DESK COMPENSATION",
        "DENTAL ASSISTANT COMPENSATION",
      ]


      // Always disable these 4 compensation rows regardless of resourceBudgetPublished status
      if (
        disabledRows.includes(row.original?.line_item) &&
        selectedClassLabel !== "PSC"
      ) {
        return false
      }

      // Header row (index 0) is not editable
      if (row.index === 0) {
        return selectedClassLabel === 'PSC'
      }
  
      return resourceBudgetPublished ? row.index != '0' : true
    },
    [resourceBudgetPublished, selectedClassLabel],
  )

  const [open, setOpen] = useState(false)
  const [confirm, setConfirm] = useState(false)

  useEffect(() => {
    if (confirm) {
      dispatch(resetColumns(null))
    }
  }, [confirm])

  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        flex: 1,
      }}
    >
      {/* {hasRevenueNotSet && (
        <Typography
          sx={{
            textAlign: "center",
            py: 1,
            width: "100%",
            color: colors.primaryBlue,
            fontWeight: "bold",
          }}
        >
          P&L budget for months which have revenue value 0 can't be set, set
          resource budget first & try again.
        </Typography>
      )} */}
      <MaterialReactTable
        key={"pnl-table"}
        columns={mutableCols}
        data={budgetData}
        editDisplayMode="cell"
        enableColumnActions={false}
        enableColumnFilters={false}
        enablePagination={false}
        enableSorting={false}
        enableBottomToolbar={false}
        enableTopToolbar={false}
        enableEditing={editable}
        enableRowVirtualization
        enablePinning
        enableStickyHeader
        enableStickyFooter
        muiTablePaperProps={{
          elevation: 0,
        }}
        renderEmptyRowsFallback={() => {
          return (
            <Box
              sx={{
                display: "flex",
                flex: 1,
                alignItems: "center",
                justifyContent: "space-evenly",
              }}
            >
              {[1, 2, 3].map((index) => (
                <Typography key={index}> No records to display</Typography>
              ))}
            </Box>
          )
        }}
        muiTableHeadCellProps={{
          align: "center",
          sx: {
            height: "48px",
            justifyContent: "center",
            backgroundColor: "#3366D6",
            color: "#fff",
            fontWeight: "bold",
            border: "0.5px solid #E4E4E4",
            boxShadow: " #00000022 1.95px  0px 2.6px",
          },
        }}
        muiTableBodyCellProps={({ table, cell }) => {
          return {
            onClick: () => {
              table.setEditingCell(cell)
            },
            sx: {
              height: "48px",
              border: "0.5px solid #E4E4E4",
              boxShadow: "#00000022 1.95px  0px 2.6px",
              color: "black",
              "&:hover": {
                borderColor: 0,
              },
            },
          }
        }}
        muiTableContainerProps={{
          sx: {
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            position: "absolute",
            borderRadius: "5px",
            height: "76vh",
          },
        }}
        muiTableBodyRowProps={{
          hover: false,
        }}
        muiEditTextFieldProps={({ cell, table }) => ({
          autoFocus: true,
          size: "small",
          onKeyDown: (event) => {
            handleKeyboardNavigation(event, cell, table)
          },
          onFocus: (event) => {
            // Select all text when cell is focused for editing
            event.target.select()
          },
          onBlur: (event) => {
            // Only update if not caused by keyboard navigation
            if (!isKeyboardNavigation) {
              if (cell.row.original.line_item == "REVENUE") {
                if (dirtyCells?.length > 1) {
                  if (
                    dirtyCells.find(
                      (dirtyCell: any) =>
                        dirtyCell == cell.id.replace("percent", "amount"),
                    )
                  ) {
                    setOpen(true)
                  }
                }
              }
              dispatch(
                updateCell({
                  key: cell.column.id,
                  index: cell.row.index,
                  updateVal: event.target.value,
                  id: cell.id,
                  original: cell.row.original,
                }),
              )
            }
            // Reset flag after handling blur
            setIsKeyboardNavigation(false)
          },
        })}
        state={{
          density: "compact",
          columnOrder: colsOrder,
          isLoading: loading,
          columnPinning: {
            left: ["line_item"],
          },
        }}
      />
      <SimpleConfirmation
        open={open}
        setOpen={setOpen}
        title="Caution!"
        optionalMessage="Resetting Revenue will clear out unsaved budget data, Do you wish to continue?"
        buttonText="Yes"
        confirmActionHandler={() => setConfirm(true)}
      />
    </Box>
  )
}

export default PNLTable
